import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, HelpCircle, MapPin, Calendar, Clock, Send, ShieldCheck, Music2, Pencil, MailCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const OPTIONS = [
  { v: "yes", label: "Yes, I can do it", icon: CheckCircle2, accent: "bg-sage-500 text-white border-sage-500", off: "border-sage-300 text-sage-700" },
  { v: "maybe", label: "Maybe / ask a question", icon: HelpCircle, accent: "bg-amber-500 text-white border-amber-500", off: "border-amber-300 text-amber-700" },
  { v: "no", label: "No, sorry", icon: XCircle, accent: "bg-ink-700 text-white border-ink-700", off: "border-ink-300 text-ink-600" },
];

export default function DepReply() {
  const { token } = useParams();
  const [search] = useSearchParams();
  const initialPick = ["yes", "no", "maybe"].includes(search.get("r")) ? search.get("r") : null;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ submitted_name: "", submitted_email: "", submitted_phone: "", message: "" });
  const [pick, setPick] = useState(initialPick);
  const [submitting, setSubmitting] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let cancelled = false;
    api.get(`/public/dep-reply/${token}`)
      .then(({ data }) => {
        if (cancelled) return;
        setData(data);
        const existing = data.response || {};
        setForm({
          submitted_name: existing.submitted_name || data.recipient?.name || "",
          submitted_email: existing.submitted_email || data.recipient?.email || "",
          submitted_phone: existing.submitted_phone || "",
          message: existing.message || "",
        });
        if (existing.response) setPick(existing.response);
      })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const validate = () => {
    const e = {};
    if (!pick) e.pick = "Choose Yes, Maybe or No";
    if (!form.submitted_name.trim()) e.submitted_name = "Please add your name so the requester knows who replied";
    if (!form.submitted_email.trim()) e.submitted_email = "Please add an email so they can get back to you";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post(`/public/dep-reply/${token}`, { ...form, response: pick });
      // Replace the form with the big confirmation screen.
      setJustSubmitted(true);
      // refetch so editing flows work for any return visit
      const { data: fresh } = await api.get(`/public/dep-reply/${token}`);
      setData(fresh);
      // Scroll to the top of the confirmation card on mobile so they SEE it
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch {}
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "already_filled") {
        const { data: fresh } = await api.get(`/public/dep-reply/${token}`);
        setData(fresh);
      } else {
        toast.error(formatApiErrorDetail(detail) || err.message);
      }
    } finally { setSubmitting(false); }
  };

  if (loading) return <Shell><div className="card-base p-6 text-ink-600">Loading…</div></Shell>;
  if (!data) return (
    <Shell>
      <div className="card-base p-6" data-testid="dep-reply-invalid">
        <div className="font-heading text-xl font-semibold mb-1">Link expired</div>
        <p className="text-sm text-ink-600">This dep request can't be found. The link may have been copied incorrectly or the requester withdrew it.</p>
      </div>
    </Shell>
  );

  const b = data.broadcast;
  const filled = b.status === "filled";
  const existing = data.response;

  // === Big success screen ===
  if (justSubmitted) {
    const opt = OPTIONS.find((o) => o.v === pick);
    return (
      <Shell>
        <div className="space-y-5" data-testid="dep-reply-success">
          <div className="card-base p-6 sm:p-8 text-center border-2 border-sage-300 bg-sage-50/60">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-sage-500 mx-auto mb-3">
              <MailCheck size={28} className="text-white" />
            </div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">Thank you. Your response has been recorded.</h1>
            <p className="text-ink-700 mt-2 text-sm">
              {b.from_name} can see your reply inside AMDS. They'll be in touch directly if they need anything else.
            </p>
            <div className="mt-4 inline-flex items-center gap-2">
              {opt && (
                <span className={`pill ${opt.accent.replace("border-", "border-").replace("text-white", "text-white")} text-xs`}>
                  <opt.icon size={12} /> Recorded as: {opt.label}
                </span>
              )}
            </div>
          </div>

          <div className="card-base p-4 sm:p-5 space-y-2">
            <Row icon={Music2} label="Instrument" value={b.instrument} />
            <Row icon={Calendar} label="Date" value={b.date} />
            {b.call_time && <Row icon={Clock} label="Call time" value={b.call_time} />}
            {b.venue && <Row icon={MapPin} label="Venue" value={b.venue} />}
          </div>

          {!filled && (
            <button type="button" onClick={() => setJustSubmitted(false)}
              className="btn-secondary mx-auto" data-testid="edit-response-btn">
              <Pencil size={14} /> Change my response
            </button>
          )}

          <div className="text-center text-xs text-ink-500 pt-4">
            AMDS — diary &amp; dep-finding for musicians
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="space-y-5" data-testid="dep-reply-page">
        <div>
          <div className="label-overline">AMDS · Dep request</div>
          <h1 className="font-heading text-2xl font-bold tracking-tight mt-1">
            {b.from_name} is looking for a {b.instrument} dep
          </h1>
          <p className="text-ink-600 mt-1 text-sm">
            One quick tap below — they'll see your reply inside AMDS.
          </p>
        </div>

        {filled && (
          <div className="card-base p-4 sm:p-5 border-l-4 border-ink-300 bg-ivory-100" data-testid="dep-filled-banner">
            <div className="font-heading font-semibold text-ink-900">This dep has been covered</div>
            <p className="text-sm text-ink-700 mt-1">Thanks for taking the time — really appreciated. {b.from_name} has confirmed someone else.</p>
          </div>
        )}

        {existing && !filled && (
          <div className="card-base p-3 border-l-4 border-sage-400 bg-sage-50/50" data-testid="existing-response-banner">
            <p className="text-sm text-ink-700">
              ✓ {b.from_name} already has your <strong>{existing.response.toUpperCase()}</strong>.
              You can update it below until they confirm a dep.
            </p>
          </div>
        )}

        {/* Event details */}
        <section className="card-base p-4 sm:p-5 space-y-2" data-testid="event-card">
          <Row icon={Music2} label="Instrument" value={b.instrument} />
          <Row icon={Calendar} label="Date" value={b.date} />
          {b.call_time && <Row icon={Clock} label="Call time" value={b.call_time} />}
          {b.venue && <Row icon={MapPin} label="Venue" value={b.venue} />}
          <Row icon={Music2} label="Type" value={b.event_type.replace("_", " ")} />
          {b.notes && (
            <div className="pt-1">
              <div className="label-overline mt-1 mb-1">Notes</div>
              <p className="text-ink-700 text-sm whitespace-pre-wrap">{b.notes}</p>
            </div>
          )}
        </section>

        {!filled && (
          <form onSubmit={submit} noValidate className="card-base p-4 sm:p-5 space-y-4" data-testid="reply-form">
            <div>
              <div className="label-overline mb-2">Your response</div>
              <div className="grid sm:grid-cols-3 gap-2" data-testid="response-options">
                {OPTIONS.map((o) => {
                  const on = pick === o.v;
                  return (
                    <button type="button" key={o.v} onClick={() => { setPick(o.v); setErrors((er) => ({ ...er, pick: undefined })); }}
                      data-testid={`response-${o.v}`}
                      className={`inline-flex items-center justify-center gap-2 rounded-full px-3 py-2.5 text-sm font-medium border-2 transition ${on ? o.accent : `bg-white ${o.off}`}`}>
                      <o.icon size={16} /> {o.label}
                    </button>
                  );
                })}
              </div>
              {errors.pick && <p className="text-sm text-terracotta-600 mt-2" data-testid="error-pick">{errors.pick}</p>}
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="label-overline block mb-2">Your name</label>
                <input className={`input-field ${errors.submitted_name ? "border-terracotta-400 bg-terracotta-50/40" : ""}`}
                  value={form.submitted_name}
                  onChange={(e) => { setForm({ ...form, submitted_name: e.target.value }); setErrors((er) => ({ ...er, submitted_name: undefined })); }}
                  data-testid="reply-name-input" />
                {errors.submitted_name && <p className="text-sm text-terracotta-600 mt-1" data-testid="error-name">{errors.submitted_name}</p>}
              </div>
              <div>
                <label className="label-overline block mb-2">Your email</label>
                <input type="email"
                  className={`input-field ${errors.submitted_email ? "border-terracotta-400 bg-terracotta-50/40" : ""}`}
                  value={form.submitted_email}
                  onChange={(e) => { setForm({ ...form, submitted_email: e.target.value }); setErrors((er) => ({ ...er, submitted_email: undefined })); }}
                  data-testid="reply-email-input" />
                {errors.submitted_email && <p className="text-sm text-terracotta-600 mt-1" data-testid="error-email">{errors.submitted_email}</p>}
              </div>
              <div className="sm:col-span-2">
                <label className="label-overline block mb-2">Phone <span className="text-ink-400 normal-case">(optional)</span></label>
                <input type="tel" className="input-field" value={form.submitted_phone}
                  onChange={(e) => setForm({ ...form, submitted_phone: e.target.value })}
                  data-testid="reply-phone-input" />
              </div>
              <div className="sm:col-span-2">
                <label className="label-overline block mb-2">Message <span className="text-ink-400 normal-case">(optional)</span></label>
                <textarea rows={3} className="input-field" placeholder="Fee, queries, anything useful…"
                  value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="reply-message-input" />
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-ink-500 inline-flex items-center gap-1.5">
                <ShieldCheck size={12} /> AMDS only shares your reply with {b.from_name}.
              </p>
              <button type="submit" disabled={submitting} className="btn-primary" data-testid="reply-submit-btn">
                <Send size={14} /> {submitting ? "Sending…" : existing ? "Update reply" : "Send reply"}
              </button>
            </div>
          </form>
        )}

        <div className="text-center text-xs text-ink-500 pt-4" data-testid="amds-footer">
          AMDS — diary &amp; dep-finding for musicians
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-ivory-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">{children}</div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={16} className="text-sage-500 mt-0.5 shrink-0" />
      <div className="text-sm">
        <span className="text-ink-500">{label}: </span>
        <span className="text-ink-900">{value}</span>
      </div>
    </div>
  );
}
